/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/referAFriend.scss';

import {ChangeFunction, Checkbox, Loader, validateEmail, voidFunction} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateButton, BolmateInput} from '../controls';

import User from '../../models/userModel';

import Image from '../misc/image';

interface Props {
    onChangeFriendEmail: ChangeFunction<string>;
    onChangePaidSubAccepted: ChangeFunction<boolean>;
    onChangeFirstPaymentAccepted: ChangeFunction<boolean>;
    onChangeAccountCreatedAccepted: ChangeFunction<boolean>;
    onSubmit: MouseEventHandler;
    friendEmail: string;
    paidSubAccepted: boolean;
    firstPaymentAccepted: boolean;
    createdAccountAccepted: boolean;
    loading: boolean;
    user: User;
}

export default function ReferAFriend(props: Props) {
    const {t} = useTranslation();
    const {
        onChangeFriendEmail,
        onChangePaidSubAccepted,
        onChangeFirstPaymentAccepted,
        onSubmit,
        friendEmail,
        paidSubAccepted,
        firstPaymentAccepted,
        loading,
        user,
        onChangeAccountCreatedAccepted,
        createdAccountAccepted
    } = props;
    const disabled = useMemo(() => {
        if (!paidSubAccepted || !firstPaymentAccepted || !createdAccountAccepted) return true;
        return !friendEmail.length || !validateEmail(friendEmail);
    }, [friendEmail, paidSubAccepted, firstPaymentAccepted, createdAccountAccepted]);

    return (
        <div className='refer-a-friend'>
            <div className='page-content-container'>
                <h1 className='page-title'>
                    <Image
                        name='logo-no-text'
                        style={{
                            maxWidth: '4rem',
                            maxHeight: '4rem',
                            objectFit: 'scale-down'
                        }}
                    />{' '}
                    {t('referAFriend.title', 'Tip een vriend(in) en krijg beide €50')}
                </h1>
                <section className='sec'>
                    <Loader active={loading} />
                    <p>
                        {t(
                            'referAFriend.intro',
                            'Sinds kort vragen we bij registratie hoe gebruikers ons hebben gevonden. Maar liefst 26% hoort van ons via een vriend(in). We willen graag samen in dit succes delen. Ontvang beide €50 tegoed in Bolmate.'
                        )}
                        <br />
                        <br />
                        <span className='sub-title'>{t('referAFriend.howItWorks', 'Hoe werkt het?')}</span>
                        <ol className='steps'>
                            <li>
                                {t(
                                    'referAFriend.step1',
                                    'Jij bent al een betalend gebruiker bij Bolmate. Laat jouw vriend(in) een betaald abonnement bij Bolmate beginnen.'
                                )}
                            </li>
                            <li>
                                {t(
                                    'referAFriend.step2',
                                    'Vul hieronder het e-mailadres in waarmee hij/zij geregistreerd is en verzend de mail.'
                                )}
                            </li>
                            <li>
                                {t(
                                    'referAFriend.step3',
                                    'Zodra jouw vriend(in) de eerste factuur heeft betaald, ontvangen jullie beide een €50 tegoed in Bolmate.'
                                )}
                            </li>
                            <li>{t('referAFriend.step4', 'Herhaal dit zo vaak als je wilt met meer vrienden!')}</li>
                        </ol>
                    </p>
                    <div className='inputs-container'>
                        <BolmateInput
                            onChange={voidFunction}
                            value={user.email}
                            label={t('referAFriend.myEmail', 'Mijn e-mailadres Bolmate')}
                            disabled
                        />
                        <BolmateInput
                            onChange={onChangeFriendEmail}
                            value={friendEmail}
                            label={t('referAFriend.friendEmail', 'Vriend(in) e-mailadres Bolmate')}
                            placeholder={t('referAFriend.emailPlaceholder', 'E-mailadres')}
                        />
                    </div>
                    <div className='terms-container'>
                        <Checkbox
                            checked={createdAccountAccepted}
                            onCheck={onChangeAccountCreatedAccepted}
                            label={t(
                                'referAFriend.terms.existingAccount',
                                'Mijn vriend(in) heeft een bestaand Bolmate account met dit e-mailadres.'
                            )}
                        />
                        <Checkbox
                            checked={paidSubAccepted}
                            onCheck={onChangePaidSubAccepted}
                            label={t(
                                'referAFriend.terms.paidSub',
                                'Ik begrijp dat zowel ik als mijn vriend(in) het abonnement gestart moeten hebben.'
                            )}
                        />
                        <Checkbox
                            checked={firstPaymentAccepted}
                            onCheck={onChangeFirstPaymentAccepted}
                            label={t(
                                'referAFriend.terms.firstPayment',
                                'Ik begrijp dat de tegoeden worden toegekend nadat mijn vriend(in) de eerste factuur heeft betaald.'
                            )}
                        />
                    </div>
                    <BolmateButton
                        onClick={onSubmit}
                        label={t('referAFriend.submit', 'Indienen')}
                        disabled={disabled}
                        className='btn-submit'
                    />
                </section>
                <p className='disclaimer'>
                    {t(
                        'referAFriend.disclaimer',
                        'Het tegoed gaat van jouw facturen af. Zo betaal je minder of niks. Referrals zijn niet te combineren met andere promoties. Zelf-referrals zijn niet toegestaan. Bolmate behoudt zich het recht voor om het programma op elk moment te wijzigen of te beëindigen. Bolmate behoudt zich het recht voor om referrals te weigeren. Bolmate behoudt zich het recht voor om de voorwaarden van dit programma op elk moment te wijzigen.'
                    )}
                </p>
            </div>
        </div>
    );
}
